import React, { useEffect, useState, useRef } from 'react'
import SBox from './opinionless/SBox'
import SInputLabel from './opinionless/SInputLabel'
import ConditionalRender from '../utils/conditionalRender'
import STooltip from './opinionless/STooltip'
import SInput from './opinionless/SInput'
import SText from './opinionless/SText'

import STheme, { SStyled, useSTheme } from 'styles/theme'
import SFlex from './opinionless/SFlex'
import { useTranslation } from '../i18n'
import { Controller, useFormContext } from 'react-hook-form'
import SInputError from './opinionless/SInputError'
import {
    AddressDetails,
    useAddressAutocompleteQueryLazyQuery,
    useAddressDetailsQueryLazyQuery,
    useAddressValidationQueryLazyQuery,
} from '../types/graphql'
import Select from 'react-select'
import { states as statesList } from 'utils/states'
import sSelectStyle from 'styles/select'
import { useDebounce } from 'utils/customHooks'
import XCard, { XCARD_TYPE } from 'v2/containers/xCard'

import styles from './style.module.scss'
import { XTEXT_SIZE } from 'v2/styles/constants'
import XColors from 'v2/styles/colors'

enum FieldKeys {
    ADDRESS_1 = 'addressLine1',
    ADDRESS_2 = 'addressLine2',
    CITY = 'city',
    STATE = 'state',
    POSTAL_CODE = 'postalCode',
}

const keyMap: Record<FieldKeys, keyof AddressDetails> = {
    [FieldKeys.ADDRESS_1]: 'address1',
    [FieldKeys.ADDRESS_2]: undefined,
    [FieldKeys.CITY]: 'city',
    [FieldKeys.STATE]: 'state',
    [FieldKeys.POSTAL_CODE]: 'postalCode',
}

interface Props {
    secondAddress?: boolean
    fontFamily: any
    userData?: any
    noTooltip?: boolean
    isV1?: boolean
    allOptional?: boolean
}

const InputRow = SStyled(SFlex)`
    flex-direction: row;

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            flex-direction: column;
        }
    `}
`

const AddressInputGroup: React.FC<Props> = (props: Props) => {
    const { secondAddress, fontFamily, isV1 } = props
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const { register, errors, setValue, trigger, getValues, control, watch } = useFormContext()
    const [addressEntered, setAddressEntered] = useState('')
    const debouncedAddress = useDebounce(addressEntered, 250)
    const [showAutocomplete, setShowAutocomplete] = useState(false)
    const [validatedAddress, setValidatedAddress] = useState(null)
    const [isAddressValidationVisible, setIsAddressValidationVisible] = useState(false)
    const [initialAddressValues, setInitialAddressValues] = useState(null)
    const [previousState, setPreviousState] = useState(null)

    let timerId = null

    const requiredString = 'required_field'

    const states = Object.keys(statesList).map((key) => {
        return {
            value: key,
            label: statesList[key],
        }
    })

    const [getAddressAutocomplete, { data: autocompleteData }] = useAddressAutocompleteQueryLazyQuery()
    const [getAddressDetails, { data: detailsData }] = useAddressDetailsQueryLazyQuery()
    const [getValidatedAddress, { data: addressValidationData }] = useAddressValidationQueryLazyQuery()

    useEffect(() => {
        if (addressEntered.length > 3) {
            getAddressAutocomplete({ variables: { value: addressEntered } })
        }
        setShowAutocomplete(addressEntered.length > 3)
    }, [debouncedAddress])

    useEffect(() => {
        const address = detailsData?.addressDetails
        updateValues(address)
    }, [detailsData])

    const updateValues = (values) => {
        if (values) {
            ;[FieldKeys.ADDRESS_1, FieldKeys.CITY, FieldKeys.POSTAL_CODE].forEach((key) =>
                fieldChanged(key, values[keyMap[key]])
            )
            fieldChanged(
                FieldKeys.STATE,
                states.find((state) => state.value == values.state)
            )
        }
    }

    const handleSearch = (event) => {
        const searchWord = event.target.value
        fieldChanged(FieldKeys.ADDRESS_1, searchWord)
        setAddressEntered(searchWord)
    }

    const handleSearchFocused = (focused) => {
        setShowAutocomplete(focused && addressEntered.length > 3)
    }

    const handleAddressClick = (placeId: string) => {
        setShowAutocomplete(false)
        getAddressDetails({ variables: { placeId } })
    }

    const fieldChanged = (key: FieldKeys, value) => {
        setValue(key, value)
        trigger(key)

        if (
            getValues(FieldKeys.ADDRESS_1) &&
            getValues(FieldKeys.CITY) &&
            getValues(FieldKeys.STATE) &&
            getValues(FieldKeys.POSTAL_CODE)
        ) {
            clearTimeout(timerId)
            timerId = setTimeout(performAddressValidation, 2000)
        }
    }

    const performAddressValidation = () => {
        getValidatedAddress({
            variables: {
                address: getValues(FieldKeys.ADDRESS_1),
                city: getValues(FieldKeys.CITY),
                state: getValues(FieldKeys.STATE)?.value,
                postalCode: getValues(FieldKeys.POSTAL_CODE),
            },
        })
    }

    function highlightDifferences(string1, string2) {
        if (string1.replace(/\s{2,}/g, ' ').trim() !== string2.replace(/\s{2,}/g, ' ').trim()) {
            return `<b>${string2}</b>`
        } else {
            return string1
        }
    }

    const autocompleteRef = useRef(null)
    const mailingAddressRef = useRef(null)

    const hasFieldsUpdated = useRef(true)

    const previousValues = useRef({
        address1: getValues(FieldKeys.ADDRESS_1),
        city: getValues(FieldKeys.CITY),
        state: getValues(FieldKeys.STATE),
        postalCode: getValues(FieldKeys.POSTAL_CODE),
    })

    useEffect(() => {
        const hasFieldsChanged =
            previousValues.current.address1 !== getValues(FieldKeys.ADDRESS_1) ||
            previousValues.current.city !== getValues(FieldKeys.CITY) ||
            previousValues.current.state !== getValues(FieldKeys.STATE) ||
            previousValues.current.postalCode !== getValues(FieldKeys.POSTAL_CODE)

        if (hasFieldsChanged) {
            hasFieldsUpdated.current = true
        }

        previousValues.current = {
            address1: getValues(FieldKeys.ADDRESS_1),
            city: getValues(FieldKeys.CITY),
            state: getValues(FieldKeys.STATE),
            postalCode: getValues(FieldKeys.POSTAL_CODE),
        }
    }, [
        getValues(FieldKeys.ADDRESS_1),
        getValues(FieldKeys.CITY),
        getValues(FieldKeys.STATE),
        getValues(FieldKeys.POSTAL_CODE),
    ])

    const handlePopUp = () => {
        const { address1, city, state, postalCode } = addressValidationData.addressValidation

        if (
            (address1.length > 0 && address1 !== getValues(FieldKeys.ADDRESS_1)) ||
            (city?.length > 0 && city !== getValues(FieldKeys.CITY)) ||
            (state?.length > 0 && state !== getValues(FieldKeys.STATE).value) ||
            (postalCode?.length > 0 && postalCode !== getValues(FieldKeys.POSTAL_CODE))
        ) {
            setValidatedAddress(
                `${highlightDifferences(getValues(FieldKeys.ADDRESS_1), address1)}${
                    getValues(FieldKeys.ADDRESS_2) ? `, ${getValues(FieldKeys.ADDRESS_2)}` : ''
                },
                ${highlightDifferences(getValues(FieldKeys.CITY), city)},
                ${highlightDifferences(getValues(FieldKeys.STATE).value, state)} ${highlightDifferences(
                    getValues(FieldKeys.POSTAL_CODE),
                    postalCode
                )}`
            )
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur()
            }
            setIsAddressValidationVisible(true)
        }
    }

    useEffect(() => {
        const handleClickOutsidemailingAddress = (event) => {
            if (
                addressValidationData &&
                hasFieldsUpdated.current &&
                !mailingAddressRef.current?.contains(event.target)
            ) {
                handlePopUp()
                hasFieldsUpdated.current = false
            }
        }

        document.addEventListener('mousedown', handleClickOutsidemailingAddress)

        return () => {
            document.removeEventListener('mousedown', handleClickOutsidemailingAddress)
        }
    }, [addressValidationData, getValues, mailingAddressRef])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
                setShowAutocomplete(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])
    const address1 = getValues(FieldKeys.ADDRESS_1)
    const address2 = getValues(FieldKeys.ADDRESS_2)
    const city = getValues(FieldKeys.CITY)
    const state = getValues(FieldKeys.STATE)?.value
    const postalCode = getValues(FieldKeys.POSTAL_CODE)

    const addressString = `${address1}${address2 ? `, ${address2}` : ''}, ${city}, ${state} ${postalCode}`
    useEffect(() => {
        const selectedValue = watch(FieldKeys.STATE)?.value
        setPreviousState(selectedValue)
    }, [watch, FieldKeys.STATE])

    useEffect(() => {
        if (getValues(FieldKeys.STATE)?.value !== previousState) {
            clearTimeout(timerId)
            timerId = setTimeout(performAddressValidation, 1)
        }
        setPreviousState(getValues(FieldKeys.STATE)?.value)
    }, [watch, FieldKeys.STATE])

    return (
        <InputRow mb={0} sx={{ position: 'relative' }}>
            <SBox flex={1} mt={5} ref={mailingAddressRef}>
                <SBox sx={{ display: 'flex !important', flexDirection: 'row !important', alignItems: 'left' }}>
                    <SInputLabel
                        sx={{
                            fontFamily: fontFamily.body,
                            fontSize: '14px',
                            fontWeight: '700',
                            width: 'auto',
                            marginRight: '3px',
                        }}
                    >
                        {t('home_address')}
                    </SInputLabel>
                    <ConditionalRender if={t('home_address_tooltip_text').length > 0 && !props?.noTooltip}>
                        <STooltip
                            sx={{ fontFamily: fontFamily.body }}
                            content={t('home_address_tooltip_text')}
                        ></STooltip>
                    </ConditionalRender>
                </SBox>
                <SBox sx={{ position: 'relative' }}>
                    <SInput
                        onChange={handleSearch}
                        name={FieldKeys.ADDRESS_1}
                        id={FieldKeys.ADDRESS_1}
                        defaultValue={props?.userData?.locationDetail?.addressLine1}
                        placeholder={t('street_address')}
                        ref={register({
                            required: props.allOptional ? false : requiredString,
                        })}
                        onBlur={() => handleSearchFocused(false)}
                        onFocus={() => handleSearchFocused(true)}
                        sx={{ fontFamily: fontFamily.body }}
                    />
                    <ConditionalRender if={showAutocomplete && autocompleteData?.addressAutocomplete?.length}>
                        <SBox
                            ref={autocompleteRef}
                            sx={{
                                marginTop: '5px',
                                borderRadius: '5px',
                                overflow: 'auto',
                                width: '100%',
                                position: 'absolute',
                                maxHeight: '150px',
                                borderWidth: '1px',
                                borderColor: 'lightgray',
                                borderStyle: 'solid',
                                backgroundColor: 'white',
                                zIndex: 200,
                            }}
                        >
                            {autocompleteData?.addressAutocomplete?.map((value, key) => {
                                return (
                                    <SText
                                        key={key}
                                        sx={{
                                            padding: '9px',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            fontFamily: fontFamily.body,
                                            boxShadow: 'main',
                                            width: '100%',
                                            '&:hover': {
                                                backgroundColor: STheme.colors.backgroundColor,
                                                color: STheme.colors.textColor,
                                            },
                                        }}
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => handleAddressClick(value.placeId)}
                                    >
                                        {value.description}
                                    </SText>
                                )
                            })}
                        </SBox>
                    </ConditionalRender>
                    {errors.addressLine1 && (
                        <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={FieldKeys.ADDRESS_1}>
                            {t(errors.addressLine1?.message)}
                        </SInputError>
                    )}
                </SBox>
                <ConditionalRender if={secondAddress}>
                    <SInput
                        onChange={(event) => fieldChanged(FieldKeys.ADDRESS_2, event.target.value)}
                        mt={5}
                        name={FieldKeys.ADDRESS_2}
                        id={FieldKeys.ADDRESS_2}
                        defaultValue={props?.userData?.locationDetail?.addressLine2}
                        placeholder={t('address_line_2_input_placeholder')}
                        {...register(FieldKeys.ADDRESS_2)}
                        ref={register()}
                        sx={{ fontFamily: fontFamily.body }}
                    />
                </ConditionalRender>
                <InputRow mt={3} justifyContent={'space-between'}>
                    <SBox flex={0.42} mt={5}>
                        <SInput
                            onChange={(event) => fieldChanged(FieldKeys.CITY, event.target.value)}
                            mr={[0, 4, 4]}
                            flex={2}
                            name={FieldKeys.CITY}
                            defaultValue={props?.userData?.locationDetail?.city}
                            id={FieldKeys.CITY}
                            placeholder={t('city_input_placeholder')}
                            ref={register({
                                required: props.allOptional ? false : requiredString,
                            })}
                            sx={{ fontFamily: fontFamily.body }}
                        />
                        {errors.city && (
                            <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={FieldKeys.CITY}>
                                {t(errors.city?.message)}
                            </SInputError>
                        )}
                    </SBox>
                    <SBox flex={0.22} mt={5}>
                        <Controller
                            name={FieldKeys.STATE}
                            as={Select}
                            id="state-field"
                            options={states}
                            control={control}
                            defaultValue={states.find((state) => state.value == props?.userData?.locationDetail?.state)}
                            rules={{ required: props.allOptional ? false : requiredString }}
                            placeholder={'CA'}
                            components={{ IndicatorSeparator: () => null }}
                            styles={{
                                ...sSelectStyle(theme),
                                container: (provided, state) => ({
                                    ...sSelectStyle(theme).container(provided, state),
                                    flex: 2,
                                    marginBottom: 3,
                                }),
                                control: (provided, state) => ({
                                    ...sSelectStyle(theme).control(provided, state),
                                    fontFamily: fontFamily.body,
                                }),
                                option: (provided, state) => ({
                                    ...sSelectStyle(theme).option(provided, state),
                                    fontFamily: fontFamily.body,
                                }),
                            }}
                        />
                        {errors.state && (
                            <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={'state-field'}>
                                {t(errors.state?.message)}
                            </SInputError>
                        )}
                    </SBox>
                    <SBox flex={0.32} mt={5}>
                        <SInput
                            onChange={(event) => fieldChanged(FieldKeys.POSTAL_CODE, event.target.value)}
                            flex={2}
                            name={FieldKeys.POSTAL_CODE}
                            defaultValue={props?.userData?.locationDetail?.zipCode}
                            id={FieldKeys.POSTAL_CODE}
                            placeholder={t('zip_code_input_placeholder')}
                            ref={register({
                                required: props.allOptional ? false : requiredString,
                                minLength: {
                                    value: 5,
                                    message: 'error_zip_code_5_digits',
                                },
                                maxLength: {
                                    value: 5,
                                    message: 'error_zip_code_5_digits',
                                },
                            })}
                            sx={{ fontFamily: fontFamily.body }}
                        />
                        {errors.postalCode && (
                            <SInputError sx={{ fontFamily: fontFamily.body }} forElementId={FieldKeys.POSTAL_CODE}>
                                {t(errors.postalCode?.message)}
                            </SInputError>
                        )}
                    </SBox>
                </InputRow>
            </SBox>
            <ConditionalRender if={isAddressValidationVisible}>
                <div className={isV1 ? styles.addressValidationV1 : styles.addressValidation}>
                    <div
                        className={
                            isV1
                                ? styles.addressValidationV1__children_containerV1
                                : styles.addressValidation__children_container
                        }
                    >
                        <XCard
                            type={XCARD_TYPE.MODAL_ADDRESS}
                            cancelButton={t('keep_original')}
                            titleSize={XTEXT_SIZE.LARGE}
                            titleColor={XColors.midGray}
                            descriptionSize={XTEXT_SIZE.MEDIUM_BIGGER}
                            descriptionColor={XColors.secondaryTextColor}
                            cancelAction={() => {
                                setIsAddressValidationVisible(false)
                                setShowAutocomplete(false)
                            }}
                            title={t('you_entered')}
                            description={[addressString]}
                        />

                        <XCard
                            type={XCARD_TYPE.MODAL_ADDRESS}
                            titleSize={XTEXT_SIZE.LARGE}
                            titleColor={XColors.midGray}
                            descriptionSize={XTEXT_SIZE.MEDIUM_BIGGER}
                            descriptionColor={XColors.secondaryTextColor}
                            continueButton={t('confirm')}
                            continueAction={() => {
                                setIsAddressValidationVisible(false)
                                setInitialAddressValues(null)
                                updateValues(addressValidationData?.addressValidation)
                                setShowAutocomplete(false)
                                timerId = null
                            }}
                            title={t('recommended')}
                            description={[validatedAddress]}
                        />
                    </div>
                </div>
            </ConditionalRender>
        </InputRow>
    )
}

export default AddressInputGroup
